import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import api, { formatDate, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { Search, Send, Mail, MessageSquare, Check, RotateCcw, AlertTriangle, Clock } from "lucide-react";

const TABS = [
  { key: "upcoming", label: "Upcoming", icon: Clock },
  { key: "overdue", label: "Overdue", icon: AlertTriangle },
  { key: "refilled", label: "Refilled history", icon: Check },
  { key: "all", label: "All tinctures" },
];

const STATUS_STYLES = {
  upcoming: "bg-status-warning-bg text-status-warning",
  overdue: "bg-status-danger-bg text-status-danger",
  refilled: "bg-status-success-bg text-status-success",
  future: "bg-slate/15 text-slate",
  no_estimate: "bg-slate/10 text-slate",
};

export default function Refills() {
  const [tab, setTab] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [composingId, setComposingId] = useState(null);
  const [composeMsg, setComposeMsg] = useState("");
  const [composeChannel, setComposeChannel] = useState("sms");
  const [busy, setBusy] = useState({});
  const [toast, setToast] = useState("");

  const load = async () => {
    const status = tab;
    const params = new URLSearchParams({ status });
    if (search.trim()) params.set("q", search.trim());
    const { data } = await api.get(`/refills?${params.toString()}`);
    setItems(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [tab, search]);

  const counts = useMemo(() => {
    // Counts only valid for the current tab's payload. Show items.length on selected tab.
    return { current: items.length };
  }, [items]);

  const sendMsg = async (id, channel, message) => {
    setBusy((b) => ({ ...b, [id]: "sending" }));
    try {
      await api.post(`/refills/${id}/send-message`, { channel, message });
      setBusy((b) => ({ ...b, [id]: "sent" }));
      setToast(`Reminder sent via ${channel}`);
      setComposingId(null); setComposeMsg("");
      setTimeout(() => setBusy((b) => { const n = { ...b }; delete n[id]; return n; }), 2000);
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      setBusy((b) => ({ ...b, [id]: "error" }));
      setToast(formatApiError(e));
      setTimeout(() => setToast(""), 3000);
    }
  };

  const markRefilled = async (id) => {
    await api.post(`/refills/${id}/mark-refilled`);
    await load();
  };

  const unmarkRefilled = async (id) => {
    await api.post(`/refills/${id}/unmark-refilled`);
    await load();
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-2">— Refills</p>
          <h1 className="font-display text-4xl text-charcoal">Refill follow-up</h1>
          <p className="text-sm font-ui text-slate mt-1">Reach clients via SMS or email when tinctures run low. {counts.current} item{counts.current === 1 ? "" : "s"} in view.</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client or tincture…"
            className="pl-9 pr-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui w-72"
            data-testid="refills-search"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-hairline mb-5 flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-ui border-b-2 -mb-px transition whitespace-nowrap flex items-center gap-1.5 ${tab === t.key ? "border-forest text-forest" : "border-transparent text-slate hover:text-charcoal"}`}
              data-testid={`refills-tab-${t.key}`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}{t.label}
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-hairline rounded-lg shadow-warm divide-y divide-hairline" data-testid="refills-list">
        {items.length === 0 && (
          <div className="px-6 py-12 text-center text-sm font-ui text-slate">
            {tab === "upcoming" && "No tinctures due for refill in the next 2 days."}
            {tab === "overdue" && "No overdue refills. Great job."}
            {tab === "refilled" && "No refilled tinctures yet."}
            {tab === "all" && "No tinctures logged."}
          </div>
        )}
        {items.map((r) => {
          const state = busy[r.id];
          const statusCls = STATUS_STYLES[r.status] || "bg-slate/15 text-slate";
          return (
            <div key={r.id} className="px-6 py-4" data-testid={`refill-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-ui text-sm text-charcoal">
                      {r.client ? <Link to={`/admin/clients/${r.client.id}`} className="hover:text-forest">{r.client.first_name} {r.client.last_initial}.</Link> : "—"}
                      <span className="text-slate"> · </span>
                      {r.product_name}
                    </p>
                    <span className={`px-2 py-0.5 rounded-pill text-[10px] font-mono uppercase tracking-wider ${statusCls}`}>{r.status}</span>
                  </div>
                  <p className="text-xs font-ui text-slate mt-0.5">
                    {r.quantity}
                    {r.estimated_refill_at && <> · Refill ~ {formatDate(r.estimated_refill_at)}</>}
                    {r.refilled_at && <> · Refilled {formatDate(r.refilled_at)}</>}
                    {r.client?.phone && <> · {r.client.phone}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.status === "refilled" ? (
                    <button onClick={() => unmarkRefilled(r.id)} className="px-3 py-1.5 text-xs font-ui border border-hairline text-slate rounded-md hover:bg-cream flex items-center gap-1.5" data-testid={`unmark-refilled-${r.id}`}>
                      <RotateCcw className="w-3 h-3" /> Undo
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => sendMsg(r.id, "sms", null)}
                        disabled={state === "sending"}
                        className="px-3 py-1.5 text-xs font-ui border border-forest text-forest rounded-md hover:bg-cream disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`send-sms-${r.id}`}
                        title="Send default SMS"
                      >
                        <MessageSquare className="w-3 h-3" /> SMS
                      </button>
                      <button
                        onClick={() => sendMsg(r.id, "email", null)}
                        disabled={state === "sending"}
                        className="px-3 py-1.5 text-xs font-ui border border-forest text-forest rounded-md hover:bg-cream disabled:opacity-50 flex items-center gap-1.5"
                        data-testid={`send-email-${r.id}`}
                        title="Send default email"
                      >
                        <Mail className="w-3 h-3" /> Email
                      </button>
                      <button
                        onClick={() => { setComposingId(composingId === r.id ? null : r.id); setComposeMsg(""); setComposeChannel("sms"); }}
                        className="px-3 py-1.5 text-xs font-ui border border-hairline text-charcoal rounded-md hover:bg-cream flex items-center gap-1.5"
                        data-testid={`compose-toggle-${r.id}`}
                      >
                        <Send className="w-3 h-3" /> Compose
                      </button>
                      <button onClick={() => markRefilled(r.id)} className="px-3 py-1.5 text-xs font-ui bg-forest hover:bg-charcoal text-white rounded-md flex items-center gap-1.5" data-testid={`mark-refilled-${r.id}`}>
                        <Check className="w-3 h-3" /> Refilled
                      </button>
                    </>
                  )}
                </div>
              </div>
              {composingId === r.id && (
                <div className="mt-3 ml-0 p-3 bg-cream/40 border border-hairline rounded-md" data-testid={`compose-panel-${r.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {[
                      { v: "sms", l: "SMS", I: MessageSquare },
                      { v: "email", l: "Email", I: Mail },
                      { v: "both", l: "Both", I: Send },
                    ].map((opt) => {
                      const I = opt.I;
                      return (
                        <button
                          key={opt.v}
                          onClick={() => setComposeChannel(opt.v)}
                          className={`px-2.5 py-1 rounded-pill text-xs font-ui border flex items-center gap-1 ${composeChannel === opt.v ? "bg-forest text-white border-forest" : "border-hairline text-charcoal hover:bg-white"}`}
                          data-testid={`compose-channel-${r.id}-${opt.v}`}
                        >
                          <I className="w-3 h-3" /> {opt.l}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    rows={3}
                    value={composeMsg}
                    onChange={(e) => setComposeMsg(e.target.value)}
                    placeholder={`Hi ${r.client?.first_name || "there"}, your ${r.product_name} is running low. Tap here to schedule a refill: rootforum.app/book`}
                    className="w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30"
                    data-testid={`compose-textarea-${r.id}`}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={() => setComposingId(null)} className="px-3 py-1.5 text-xs font-ui border border-hairline text-charcoal rounded-md hover:bg-white">Cancel</button>
                    <button
                      onClick={() => sendMsg(r.id, composeChannel, composeMsg)}
                      disabled={state === "sending"}
                      className="px-3 py-1.5 text-xs font-ui bg-forest hover:bg-charcoal text-white rounded-md disabled:opacity-40"
                      data-testid={`compose-send-${r.id}`}
                    >
                      Send via {composeChannel}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 px-4 py-2.5 bg-charcoal text-white text-sm font-ui rounded-md shadow-xl" data-testid="refills-toast">
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}
